import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";

/**
 * La pantalla de una dirección que no existe.
 *
 * NACE EL 7 DE OCTUBRE DE 2026, y lo que la abre es un hallazgo de la tarea W1: **esta casa no
 * tenía ruta `*`**. O sea que una dirección desconocida —un enlace viejo, una letra de más al
 * teclear, un enlace mal pegado en un correo— pintaba el menú, el pie y **nada en medio**. Una
 * página en blanco no dice «te has equivocado»: dice «esto está roto», y quien la ve no tiene
 * forma de saber cuál de las dos cosas es.
 *
 * Y ES LO QUE VA ANTES DE QUITAR CUALQUIER REDIRECCIÓN. El recuento de octubre enseñó seis
 * direcciones viejas que ya no usa nadie, y quitarlas sin esto convierte cada enlace olvidado
 * de por ahí en un blanco. Con esto, lo peor que le pasa a quien llegue es que se lo digan.
 *
 * LAS TRES COSAS QUE HACE, EN ESTE ORDEN:
 *
 *   1. **Decir qué ha pasado sin culpar a nadie.** No se sabe si se equivocó quien escribió la
 *      dirección o quien puso el enlace, así que no se insinúa ninguna de las dos.
 *   2. **Enseñar la dirección que pidió.** Es lo que convierte «no funciona» en algo que se
 *      puede contar: quien escribe a decir que algo está roto puede copiar esa línea.
 *   3. **Dar dos puertas de verdad** —los cursos y la portada—, que es lo que hace falta para
 *      no acabar aquí otra vez.
 *
 * Y NO LLEVA BUSCADOR a propósito: el buscador vive en los cursos, y mandar a alguien a buscar
 * desde una pantalla de error es pedirle que adivine el nombre de lo que no ha encontrado.
 *
 * LO QUE YA SE MIDE SOLO. `CuentaPantallas` apunta la dirección fallida como a cualquier otra
 * pantalla —normalizada y sin saber de quién—, así que **un enlace roto que use alguien se ve
 * en `backend/tools/mira_uso.py`**: sale contado y no declarado en `App.js`. Eso es lo que hace
 * que quitar una redirección deje de ser un acto de fe.
 */
export default function NoEncontrada() {
  const { pathname } = useLocation();
  const pedida = pathname.length > 64 ? pathname.slice(0, 64) + "…" : pathname;

  return (
    <section className="max-w-[760px] mx-auto px-6 lg:px-10 py-20" data-testid="page-404">
      <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/45 mb-4">
        Esta dirección no existe
      </div>
      <h1 className="font-serif-display text-4xl leading-tight text-alpha-black mb-4">
        Aquí no hay nada
      </h1>
      <p className="text-[16px] leading-relaxed text-alpha-black/75 max-w-[62ch] mb-6">
        La dirección que has pedido no lleva a ninguna parte. Puede que la página se llame ahora
        de otra manera, o que el enlace que has pulsado sea de hace tiempo.
      </p>

      <div className="font-mono-alpha text-[12px] text-alpha-black/55 border-l-2 border-alpha-black/10 pl-4 mb-10 break-all">
        {pedida}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <Link
          to="/cursos"
          data-testid="404-cursos"
          className="inline-flex items-center gap-2 rounded-full bg-alpha-red px-5 py-2.5 text-[12px] font-mono-alpha uppercase tracking-wide text-white"
        >
          Explora los cursos
          <ArrowRight className="size-3.5" />
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-alpha-black/15 px-4 py-2 text-[12px] font-mono-alpha uppercase tracking-wide text-alpha-black/65 hover:border-alpha-black/40 hover:text-alpha-black transition-colors"
        >
          <Compass className="size-3.5" />
          La portada
        </Link>
      </div>

      <p className="text-[13px] leading-relaxed text-alpha-black/50 max-w-[62ch]">
        Si has llegado hasta aquí desde un enlace de la Academia, el fallo es mío y quiero
        saberlo: cuéntamelo en <Link to="/contacto" className="underline">contacto</Link> y copia
        la línea de arriba.
      </p>
    </section>
  );
}
